import React, { useState } from 'react';
import { Button, Form, Space, message } from 'antd';
import { userTfaEnable, userTfaVerify } from '@/services/api';
import styles from './tfa.less';
import { ModalForm } from '@ant-design/pro-form';
import { makeBackupCodesSubmitter, makeBackupCodesComponents } from './BackupCodes';
import { makeQRCodeSection, makeSubDescriptionStep2, makeTfaSetupComponents } from './TfaSetupComponents';
import { TfaCodeComponent } from './tfa';
import { t } from '@/global';

enum EnableTfaState {
    Verify = 0,
    Enable = 1,
    ShowBackupCodes = 2,
}

type TfaFormProps = {
    otpauth?: string;
    modalOpen: boolean;
    handleModalOpen: (open: boolean, tfa_updated: boolean) => void;
    plain: (text: string, defaultMessage?: string) => string;
};

const EnableTfaForm: React.FC<TfaFormProps> = ({ otpauth, modalOpen, handleModalOpen, plain }) => {
    const [form] = Form.useForm();
    const [isCodeReady, setIsCodeReady] = useState<boolean>(false);
    const [enableState, setEnableState] = useState<EnableTfaState>(otpauth == undefined ? EnableTfaState.Verify : EnableTfaState.Enable);
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [otpauthUrl, setOtpauthUrl] = useState<string | undefined>(otpauth);
    const [newTfaStatus, setNewTfaStatus] = useState<number | undefined>(undefined);
    const [err, setErr] = useState<string | undefined>(undefined);

    const makeSubDescription = () => <div className={styles.desc}>{t('Make your RustDesk account more secure by requiring an extra authentication code when you log in.')}</div>;


    const makeCodeComponent = () => <div style={{ marginLeft: '15px' }}>
        <TfaCodeComponent
            form={form}
            setIsCodeReady={setIsCodeReady}
            width={240}
            isBackupCodeAcceptable={false}
            err={err}
            setErr={setErr}
        />
    </div>;

    const handleSubmit = async (value: any) => {
        if (enableState === EnableTfaState.Verify) {
            const otpauth_url = await userTfaEnable(value);
            setOtpauthUrl(otpauth_url);
            setEnableState(EnableTfaState.Enable);
            form.setFieldValue('tfa_code', '');
            setNewTfaStatus(0);
        } else {
            try {
                await form.validateFields();
            } catch (error) {
                return;
            }

            try {
                const resp = await userTfaVerify(value);
                if (resp.success) {
                    message.success('Enable 2FA success!');
                    setBackupCodes(resp.backup_codes ?? []);
                    setEnableState(EnableTfaState.ShowBackupCodes);
                    setNewTfaStatus(2);
                } else {
                    setErr('Can\'t verify the code. Check that code and local time settings are correct!');
                }
            }
            catch (error: any) {
                message.error(error);
            }
        }
    }


    const makeEnableSubmitter = () => {
        return {
            render: (props: any, dom: JSX.Element[]) => {
                return <Space>
                    {dom[0]}
                    <Button
                        type="primary"
                        onClick={() => form.submit()}
                        disabled={!isCodeReady}
                    >
                        {plain('Enable 2FA')}
                    </Button>
                </Space>;
            },
        };
    };

    const makeRemark = () => (
        <>
            <br />
            <br />
            <div className={styles.remark}>
                {t('You can change or remove 2FA at any time in your account settings.')}
            </div>
        </>
    );

    const makeComponentStep2 = () => (
        <div className={styles.step2}>
            <div>
                {makeSubDescriptionStep2()}
                <div style={{ height: '15px' }} />
                {makeCodeComponent()}
                {makeRemark()}
            </div>
            <div>
                {makeQRCodeSection((otpauth || otpauthUrl)!)}
            </div>
        </div>
    );

    const makeNewComponents = () => <>
        {makeSubDescription()}
        {makeTfaSetupComponents()}
        {makeComponentStep2()}
    </>;

    const makeShowBackupCodesComponents = () => <>
        {makeBackupCodesComponents(plain, backupCodes)}
    </>;

    const handleModalOpen2 = (v: boolean) => {
        if (!v) {
            form.setFieldsValue({ tfa_code: '' });
        }
        handleModalOpen(v, !v && newTfaStatus != undefined);
    }

    const getSubmitter = () => {
        if (enableState === EnableTfaState.Verify) {
            return undefined;
        } else if (enableState === EnableTfaState.Enable) {
            return makeEnableSubmitter();
        } else {
            return makeBackupCodesSubmitter(plain, backupCodes, handleModalOpen2);
        }
    }

    const getComponents = () => {
        if (enableState === EnableTfaState.Verify) {
            return <>
                <div style={{ marginBottom: '15px' }}>{t('Please verify your 2FA code to disable current 2FA first.')}</div>
                <TfaCodeComponent
                    form={form}
                    isBackupCodeAcceptable={true}
                    err={err}
                    setErr={setErr}
                />
            </>;
        } else if (enableState === EnableTfaState.Enable) {
            return makeNewComponents();
        } else {
            return makeShowBackupCodesComponents();
        }
    }

    return modalOpen && <ModalForm
        title={t('Enable Two-Factor Authentication')}
        open={modalOpen}
        onOpenChange={handleModalOpen2}
        width='620px'
        form={form}
        className={styles.modal}
        onFinish={handleSubmit}
        submitter={getSubmitter()}
    >
        {getComponents()}
    </ModalForm>;
}

export default EnableTfaForm;
